import type {
	KYCModel,
	KYCQuestionComponentProps,
	KYCRequestBody,
	KYCOption,
	KYCQuestionProps,
	KYCOptionAPI,
	SpecificValue,
	KYCDepend
} from '@app/models/api/KYCModel';

import { translateKYCModel, translateQuestionTypeToAPI } from '@app/models/api/KYCModel';
import { KYCQuestionType, KYCCategoriesName } from '@app/enums/KYC';

import InputRadios from '$lib/elements/inputs/variants/InputRadios.svelte';
import InputNumber from '$lib/elements/inputs/variants/InputNumber.svelte';
import InputsCheckbox from '$lib/elements/inputs/variants/InputsCheckbox.svelte';
import InputSelect from '$lib/elements/inputs/variants/InputSelect.svelte';
import InputDocumentKYC from '$lib/components/InputDocumentKYC.svelte';
import InputText from '$lib/elements/inputs/variants/InputText.svelte';
import InputSelectCountry from '@app/components/inputs/InputSelectCountry.svelte';
import InputDate from '$lib/elements/inputs/variants/InputDate.svelte';
import KYCIntroductionStep from '@app/components/kyc/KYCIntroductionStep.svelte';
import InputCityKYCAdapter from '@app/components/inputs/InputCityKYCAdapter.svelte';
import InputAddressProofKYC from '@app/components/inputs/InputAddressProofKYC.svelte';
import InputAddressKYCAdapter from '@app/components/inputs/InputAddressKYCAdapter.svelte';
import InputLegalInformationCompany from '@app/components/inputs/InputLegalInformationCompany.svelte';
import InputTelKYCAdapter from '@app/components/inputs/InputTelKYCAdapter.svelte';
import InputPPEKYC from '@app/components/inputs/InputPPEKYC.svelte';
import { snackbar } from '$lib/stores/snackbar';

import { APIEndpoints } from '@app/enums/APIEndpoints';
import {
	amountSavesValues,
	amountHeritageOthersValues,
	amountHeritageMoneyValues,
	amountHeritageRealEstateValues,
	amountHeritagePelAndCoValues
} from '@app/enums/FinancialSituationValue';
import { get } from 'svelte/store';
import { t } from 'svelte-i18n';
import { formatAmountWithCurrency } from '$lib/utils/strings';
import { configBeneficiary } from '@app/KYCDatasSteps';
import type { AccountModel } from '@app/models/api/AccountModel';
import type { PhysicalPersonPartner } from '@app/models/api/PhysicalPersonPartner';
import { DocumentStatusEnum } from '@app/models/api/enums/DocumentStatusEnum';
import type { DocumentsKYCModel } from '@app/models/api/DocumentsKYCModel';
import type { InputFilesProps } from '$lib/elements/inputs/types';
import { error, type HttpError } from '@sveltejs/kit';
import { invalidateAll } from '$app/navigation';
import type { OperationRegulationsEnum } from '@app/models/api/enums/OperationRegulationsEnum';
import type { Fetch } from '@app/types/Global';
import { operationStore } from '@app/stores/operation';
import { urls } from '@app/urls';
import { page } from '$app/stores';
import { browser } from '$app/environment';
import { isDateBefore } from '@app/lib/utils/date';

export const definedQuestions = (kycDatas: KYCModel, isKYCDatas = true): KYCModel => {
	const $t = get(t);
	const questions: KYCQuestionComponentProps[] = [];
	if (!kycDatas.questions) return kycDatas;
	kycDatas.questions.forEach((element: KYCQuestionProps) => {
		if (!element.type) return;
		switch (element.type) {
			case KYCQuestionType.MULTIPLE_QUESTION:
				if (element.questions) {
					questions.push({
						component: null,
						props: {
							...element,
							required: true,
							questions: definedQuestions({
								name: KYCCategoriesName.AdditionalQuestions,
								questions: element.questions
							}).questions
						}
					});
				}

				break;

			case KYCQuestionType.MULTIPLE_CHOICE:
				questions.push({
					component: InputsCheckbox,
					props: {
						...element,
						renderSpecificValue: true,
						specificValue: element?.customerResponse?.responses
							? [...(element?.customerResponse?.responses as SpecificValue[])]
							: null,
						value: element?.customerResponse?.responses || '',
						column: element.column !== false,
						required: true,
						label: isKYCDatas ? element.label : element.title,
						classNames: isKYCDatas ? 'flex flex-col items-center' : ''
					}
				});

				break;

			case KYCQuestionType.SINGLE_CHOICE:
				questions.push({
					component: InputRadios,
					props: {
						...element,
						renderSpecificValue: true,
						specificValue: (element?.customerResponse?.responses as SpecificValue) || null,
						required: true,
						label: isKYCDatas ? element.label : element.title,
						column: element.column !== false,
						classNames: isKYCDatas ? 'flex flex-col items-center' : '',
						value: element?.customerResponse?.responses?.value || ''
					}
				});
				break;

			case KYCQuestionType.INPUT_TEXT:
				questions.push({
					component: InputText,
					props: {
						...element,
						value: element?.customerResponse?.response || '',
						required: element.required ?? true
					}
				});
				break;
			case KYCQuestionType.INPUT_TEL:
				questions.push({
					component: InputTelKYCAdapter,
					props: {
						...element,
						value: element?.customerResponse?.response || '',
						required: element.required ?? true,
						questions: definedQuestions({
							name: KYCCategoriesName.AdditionalQuestions,
							questions: element.questions
						}).questions
					}
				});
				break;
			case KYCQuestionType.INPUT_NUMBER:
				questions.push({
					component: InputNumber,
					props: {
						...element,
						label: isKYCDatas ? element.label : element.title,
						value: element?.customerResponse?.response || '',
						required: element.required ?? true,
						placeholder: element?.placeholder ?? $t('kyc.defaultPlaceholderInputNumber')
					}
				});

				break;
			case KYCQuestionType.INPUT_SELECT:
				questions.push({
					component: InputSelect,
					props: {
						...element,
						value:
							element?.multiple && element?.customerResponse?.response
								? [element?.customerResponse?.response]
								: element?.multiple
									? []
									: element?.customerResponse?.response
										? element?.customerResponse?.response
										: '',
						required: element.required ?? true
					}
				});

				break;
			case KYCQuestionType.INPUT_COUNTRY:
				questions.push({
					component: InputSelectCountry,
					props: {
						...element,
						value: element?.customerResponse?.response || '',
						required: element.required ?? true
					}
				});
				break;
			case KYCQuestionType.INPUT_CITY:
				if (element.questions) {
					questions.push({
						component: InputCityKYCAdapter,
						props: {
							...element,
							value: element?.customerResponse?.response || '',
							required: element.required ?? true,
							questions: definedQuestions({
								name: KYCCategoriesName.AdditionalQuestions,
								questions: element.questions
							}).questions
						}
					});
				}

				break;
			case KYCQuestionType.INPUT_ADDRESS:
				if (element.questions) {
					questions.push({
						component: InputAddressKYCAdapter,
						props: {
							...element,
							required: element.required ?? true,
							value: element?.customerResponse?.response || '',
							questions: definedQuestions({
								name: KYCCategoriesName.AdditionalQuestions,
								questions: element.questions
							}).questions
						}
					});
				}

				break;

			case KYCQuestionType.INPUT_ADDRESSPROOF:
				if (element.questions) {
					questions.push({
						component: InputAddressProofKYC,
						props: {
							...element,
							required: element.required ?? true,
							readonly: !!element?.customerResponse?.response?.file,
							accept: 'application/pdf, image/png, image/jpeg, image/jpg, image/gif, image/heic',
							value: element?.customerResponse?.response || '',
							questions: definedQuestions({
								name: KYCCategoriesName.AdditionalQuestions,
								questions: element.questions
							}).questions
						}
					});
				}

				break;
			case KYCQuestionType.INPUT_PPE:
				if (element.questions) {
					questions.push({
						component: InputPPEKYC,
						props: {
							...element,
							required: element.required ?? true,
							column: element.column !== false,
							value: element?.customerResponse || '',
							classNames: element.classNames ?? '',
							questions: definedQuestions({
								name: KYCCategoriesName.AdditionalQuestions,
								questions: element.questions
							}).questions
						}
					});
				}

				break;
			case KYCQuestionType.INPUT_LEGAL_INFORMATION_COMPANY:
				questions.push({
					component: InputLegalInformationCompany,
					props: {
						...element,
						column: element.column !== false,
						value: element?.customerResponse?.response || '',
						questions: definedQuestions({
							name: KYCCategoriesName.AdditionalQuestions,
							questions: element.questions
						}).questions
					}
				});

				break;
			case KYCQuestionType.INPUT_DATE:
				questions.push({
					component: InputDate,
					props: {
						...element,
						value: element?.customerResponse?.response || '',
						required: element.required ?? true
					}
				});

				break;
			case KYCQuestionType.INPUT_HIDDEN:
				questions.push({
					component: null,
					props: {
						...element,
						value: element?.customerResponse?.response || ''
					}
				});

				break;
			case KYCQuestionType.BENEFICIARY_STEP:
				questions.push({
					component: KYCBeneficiaryStep,
					props: {
						...element,
						required: true,
						questions: definedQuestions({
							name: KYCCategoriesName.AdditionalQuestions,
							questions: element.questions
						}).questions
					}
				});

				break;
			case KYCQuestionType.INPUT_FILE:
				questions.push({
					component: InputDocumentKYC,
					props: {
						...element,
						value: element?.customerResponse?.response || '',
						required: element?.required ?? true,
						questions: definedQuestions({
							name: KYCCategoriesName.AdditionalQuestions,
							questions: element.questions
						}).questions
					}
				});
				break;
			case KYCQuestionType.INTRODUCTION:
				questions.push({
					component: KYCIntroductionStep,
					props: {
						...element,
						value: element?.customerResponse?.response || '',
						required: element.required ?? true
					}
				});

				break;
		}
	});
	return { ...kycDatas, questions };
};

export const hasResponseChanged = (question: KYCQuestionProps) => {
	let hasChanged = false;
	switch (question.type) {
		case KYCQuestionType.SINGLE_CHOICE:
		case KYCQuestionType.MULTIPLE_CHOICE: {
			const hasDifferentResponse =
				question.customerResponse &&
				question.specificValue &&
				Object.keys(question.specificValue).length !== 0 &&
				JSON.stringify(question.customerResponse.responses) !==
					JSON.stringify(question.specificValue);

			if (hasDifferentResponse) {
				hasChanged = true;
			}
			break;
		}
		case KYCQuestionType.INPUT_TEXT:
		case KYCQuestionType.INPUT_NUMBER: {
			const hasDifferentResponse =
				question.customerResponse &&
				question.value &&
				question.customerResponse.response !== question.value;

			if (hasDifferentResponse) {
				hasChanged = true;
			}
			break;
		}
	}

	return hasChanged;
};

export const createResponseTemplate = (
	questions: KYCQuestionComponentProps[],
	regulation: OperationRegulationsEnum
): KYCRequestBody => {
	const responseTemplate: KYCRequestBody = {
		type: regulation,
		questions: []
	};

	questions?.map((question: KYCQuestionComponentProps) => {
		if (hasResponseChanged(question?.props) && question?.props?.isVisible) {
			const response = {
				...transformKYCQuestionPropsToAPI(question?.props),
				id: question?.props?.id,
				type: question?.props?.type ? translateQuestionTypeToAPI(question?.props?.type) : undefined,
				libelle: question?.props?.title
			};
			responseTemplate.questions.push(response);
		}
	});
	return responseTemplate;
};

export const transformKYCQuestionPropsToAPI = (props: KYCQuestionProps) => {
	switch (props.type) {
		case KYCQuestionType.MULTIPLE_CHOICE:
			return { reponses: props.specificValue ? transformKYCOptionsToAPI(props.specificValue) : [] };
		case KYCQuestionType.SINGLE_CHOICE:
			return {
				reponses: [{ id: props.specificValue?.id, libelle: props.specificValue?.value }]
			};
		case KYCQuestionType.INPUT_TEXT:
		case KYCQuestionType.INPUT_NUMBER:
			return { reponseLibre: props.value };
	}
};

export const transformKYCOptionsToAPI = (
	options: KYCOption[] | SpecificValue[]
): KYCOptionAPI[] => {
	let result: KYCOptionAPI[] = [];
	options.map((option: KYCOption) => {
		const apiOption: KYCOptionAPI = {
			id: option.id,
			libelle: option?.value
		};
		result = [...result, apiOption];
	});
	return result;
};
export const getAndTranslateKYCData = async (
	regulation: OperationRegulationsEnum
): Promise<KYCModel[]> => {
	const datas = await getKYCContent(regulation, fetch);
	datas.questions = [];

	const newDatas: KYCModel[] = [];
	datas.categories.forEach((data) => {
		newDatas.push(translateKYCModel(data));
	});
	return newDatas;
};
export const filterQuestionsByValidityDate = (kycData: KYCModel[]): KYCModel[] => {
	const today = new Date();
	return kycData.map((category) => {
		return {
			...category,
			questions: category.questions?.map((question) => {
				const validityDate = question.customerResponse?.validityDate
					? new Date(question.customerResponse.validityDate)
					: null;
				if (validityDate && isDateBefore(validityDate)) {
					return { ...question, customerResponse: undefined };
				}
				return question;
			})
		};
	});
};
export const organizeQuestions = (datas: KYCModel[]): KYCModel[] => {
	const newDatas: KYCModel[] = [];
	datas.forEach((data) => {
		newDatas.push(definedQuestions(data, false));
	});
	return newDatas;
};

export const sortQuestionsByOrder = (
	questions: KYCQuestionComponentProps[],
	sortOrder: 'ASC' | 'DESC' = 'ASC'
): KYCQuestionComponentProps[] => {
	const compareFunction = (a: KYCQuestionComponentProps, b: KYCQuestionComponentProps) => {
		if (sortOrder === 'ASC') {
			return a.props.order - b.props.order;
		} else {
			return b.props.order - a.props.order;
		}
	};

	return [...questions].sort(compareFunction);
};

export const getKYCContent = async (regulation: OperationRegulationsEnum, fetch?: Fetch) => {
	const res = await fetch(APIEndpoints.ACCOUNT(`kyc?typeParcours=${regulation}`));
	const json = await res.json();
	if (res.ok) {
		return json;
	} else {
		throw json;
	}
};

export const getAndTranslateKYCDataByReglementation = async (
	regulation: OperationRegulationsEnum,
	fetch: Fetch
): Promise<KYCModel[]> => {
	const questions = await getKYCContent(regulation, fetch);
	questions.questions = null;

	const result: KYCModel[] = [];
	if (questions && questions.categories) {
		questions.categories.map((question) => {
			result.push(translateKYCModel(question));
		});
	}
	result.forEach(({ questions }) => {
		questions?.forEach((questionMain) => {
			questionMain?.dependsOn?.forEach(({ label, idQuestion }) => {
				const matchingQuestion = questions?.find(({ id }) => id === idQuestion);
				if (matchingQuestion?.customerResponse?.responses?.label === label) {
					questionMain.isVisible = true;
				}
			});
		});
	});

	return result;
};

export const toggleQuestionVisibility = (
	question: KYCQuestionProps,
	isVisible: boolean,
	required: boolean
) => {
	question.isVisible = isVisible;
	question.required = required;
};
export const processDependentQuestions = (questions: KYCQuestionComponentProps[]) => {
	if (questions) {
		questions.forEach((question: KYCQuestionComponentProps) => {
			const conditionValueForShow: string[] = [];
			if (question?.props?.dependsOn) {
				question.props.dependsOn.forEach((depend) => {
					const dependentQuestion = getQuestionByDepend(depend, questions);
					if (dependentQuestion) {
						if (dependentQuestion.value) {
							if (!Array.isArray(dependentQuestion.value)) {
								conditionValueForShow.push(depend.label);
								const haveMatch = conditionValueForShow.some((conditionValue) =>
									dependentQuestion?.specificValue?.value?.includes(conditionValue)
								);
								if (haveMatch) {
									toggleQuestionVisibility(question.props, true, true);
								} else {
									toggleQuestionVisibility(question.props, false, false);
								}
							} else {
								if (dependentQuestion.value.includes(depend?.label)) {
									toggleQuestionVisibility(question.props, true, true);
								} else {
									toggleQuestionVisibility(question.props, false, false);
								}
							}
						}
					}
				});
			}
		});
	}
};

export const getQuestionByDepend = (depend: KYCDepend, questions: KYCQuestionComponentProps[]) => {
	return questions.find((question) => question.props.id === depend.idQuestion)?.props || null;
};

export const updateRequiredStatus = (questionsCategories: KYCModel[]) => {
	if (questionsCategories) {
		questionsCategories.forEach((categories) => {
			if (categories?.questions) {
				categories?.questions.forEach((question) => {
					if (question?.props && !question?.props?.isVisible) {
						question.props.required = false;
					}
				});
			}
		});
	}
	return questionsCategories;
};

export const getIndexStepByCategorieName = (
	questionsData,
	categorieName: KYCCategoriesName
): number => {
	return questionsData.findIndex((e) => e.name === categorieName);
};

export const getProgressBarData = (questions: KYCModel[]) => {
	return questions.map((categorie) => ({
		name: categorie.name,
		quantity: categorie.questions ? categorie.questions.length : 0
	}));
};

export const doesCategoryExist = (questionsData: KYCModel[], categoryName: string): boolean => {
	return questionsData.some((item) => item.name === categoryName);
};

export const insertObjectAtIndex = (
	dataArray: KYCModel[],
	index: number,
	objectToInsert: KYCModel
): KYCModel[] => {
	dataArray.splice(index, 0, objectToInsert);
	return dataArray;
};

export const removeElementByName = (dataArray: KYCModel[], name: string): KYCModel[] => {
	return dataArray.filter((item) => item.name !== name);
};

export const buildApiPayload = (requestData: any) => {
	const requestBody: any = {};

	const processItem = (item: any) => {
		const { apiKeys, value, type } = item;
		const typeIsAccepted =
			type !== KYCQuestionType.INPUT_FILE && type !== KYCQuestionType.INPUT_ADDRESSPROOF;
		if (apiKeys && value !== null && value !== undefined) {
			let nestedObject: any = requestBody;

			apiKeys.forEach((key: string, index: number) => {
				if (typeIsAccepted) {
					if (index === apiKeys.length - 1) {
						nestedObject[key] = value;
					} else {
						nestedObject[key] = nestedObject[key] || {};
						nestedObject = nestedObject[key];
					}
				}
			});
		}
	};

	if (Array.isArray(requestData)) {
		requestData.forEach((element: any) => {
			processItem(element.props);
		});
	} else {
		processItem(requestData);
	}
	return requestBody;
};

export const mergeObjects = (...objects: any[]) => {
	const merged = {};

	objects.forEach((obj) => {
		for (let key in obj) {
			if (obj.hasOwnProperty(key)) {
				if (
					merged.hasOwnProperty(key) &&
					typeof merged[key] === 'object' &&
					typeof obj[key] === 'object'
				) {
					merged[key] = mergeObjects(merged[key], obj[key]);
				} else {
					merged[key] = obj[key];
				}
			}
		}
	});

	return merged;
};

export const findPropsByApiKey = (
	questions: KYCQuestionProps[],
	key: string
): KYCQuestionProps | undefined => {
	return questions?.find((element) => element?.props?.apiKeys?.includes(key))?.props;
};
export const toBase64 = (file: Blob) =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result);
		reader.onerror = (error) => reject(new Error(`${error}`));
	});

export const createBeneficiaryInformationAPIBody = async (
	beneficiaryQuestions: KYCQuestionProps[]
) => {
	const dataInformation: any[] = [];
	const dataDocuments: any[] = [];
	for (const element of beneficiaryQuestions) {
		if (
			element.props?.type === KYCQuestionType.MULTIPLE_QUESTION &&
			!element?.props?.apiKeys?.includes('businessResults') &&
			!element?.props?.apiKeys?.includes('businessRevenue')
		) {
			dataInformation.push(buildApiPayload(element.props.questions));
		} else if (element.props?.apiKeys?.includes('cni') && !element?.props?.readonly) {
			dataDocuments.push({
				cni: (await toBase64(element?.props?.value?.[0] as Blob)).split(',')[1]
			});
		}
	}
	const result = {
		personneMorale: {
			personnesPhysiquesAssociees: dataInformation.map((personne, index) => {
				return {
					informations: {
						...personne.informations
					},
					documents: {
						cni: dataDocuments[index]?.cni
					}
				};
			})
		}
	};

	return result;
};
export const countSimulatorResult = (
	annualNetIncomeValue: number,
	liquidAssetAmountValue: number,
	annualFinancialCommitmentAmountValue: number
) => {
	return annualNetIncomeValue + liquidAssetAmountValue - annualFinancialCommitmentAmountValue;
};
export const parseFinancialSituation = (stepQuestions: KYCQuestionProps[]) => {
	const $t = get(t);

	const amountHeritageRealEstateProps = findPropsByApiKey(
		stepQuestions,
		'tranchePatrimoineImmobilier_E1_1'
	);
	const amountHeritageMoneyProps = findPropsByApiKey(
		stepQuestions,
		'tranchePatrimoineLiquidites_E1_2'
	);
	const amountHeritagePelAndCoProps = findPropsByApiKey(
		stepQuestions,
		'tranchePatrimoineFinancier_E1_3'
	);
	const amountHeritageOthersProps = findPropsByApiKey(
		stepQuestions,
		'tranchePatrimoineAutres_E1_4'
	);
	const canLose10PercentProps = findPropsByApiKey(
		stepQuestions,
		'pensePouvoirSubirPerte10Pourcent_E6'
	);
	const amountSaveProps = findPropsByApiKey(stepQuestions, 'trancheCapaciteEpargneMensuelle_E3_3');
	const saves =
		amountSaveProps?.value && !isNaN(amountSaveProps.value as number)
			? (amountSavesValues[amountSaveProps.value] as number)
			: 0;

	const heritage =
		(amountHeritageRealEstateProps?.value
			? amountHeritageRealEstateValues[amountHeritageRealEstateProps.value]
			: 0) +
		(amountHeritageMoneyProps?.value
			? amountHeritageMoneyValues[amountHeritageMoneyProps.value]
			: 0) +
		(amountHeritagePelAndCoProps?.value
			? amountHeritagePelAndCoValues[amountHeritagePelAndCoProps.value]
			: 0) +
		(amountHeritageOthersProps?.value
			? amountHeritageOthersValues[amountHeritageOthersProps.value]
			: 0);
	const savesPlusHeritage = saves + heritage;

	canLose10PercentProps.title = $t('kyc.inputs.canLose10Percent', {
		values: {
			liquidityPlusHeritage: formatAmountWithCurrency(savesPlusHeritage),
			maxLose: formatAmountWithCurrency(savesPlusHeritage / 10)
		}
	});
};

export const parseSimulatorData = (stepQuestions: KYCQuestionProps[]) => {
	const $t = get(t);
	const montantRevenusAnnuelsProps = findPropsByApiKey(stepQuestions, 'montantRevenusAnnuels');
	const montantActifsLiquidesProps = findPropsByApiKey(stepQuestions, 'montantActifsLiquides');
	const montantEngagementsFinanciersProps = findPropsByApiKey(
		stepQuestions,
		'montantEngagementsFinanciers'
	);

	const currentSimulatorStep = countSimulatorResult(
		parseInt(montantRevenusAnnuelsProps?.value || 0, 10),
		parseInt(montantActifsLiquidesProps?.value || 0, 10),
		parseInt(montantEngagementsFinanciersProps?.value || 0, 10)
	);
	const parseAndFormatAmount = (apiKey: string) => {
		const propValue = findPropsByApiKey(stepQuestions, apiKey)?.value;
		return formatAmountWithCurrency(propValue) || 0;
	};

	const recuResultatsSimulationValue =
		Array.isArray(findPropsByApiKey(stepQuestions, 'recuResultatsSimulation')?.value) &&
		findPropsByApiKey(stepQuestions, 'recuResultatsSimulation')?.value[0] === 'yes';

	const partageDonneesFinancieresValue =
		findPropsByApiKey(stepQuestions, 'partageDonneesFinancieres')?.value === 'yes';

	const recuResultatsSimulationProps = findPropsByApiKey(stepQuestions, 'recuResultatsSimulation');
	if (recuResultatsSimulationProps) {
		const introductionMessage = $t('kyc.steps.simulator.result.info', {
			values: {
				totalAmount: formatAmountWithCurrency(currentSimulatorStep || 0),
				annualNetIncomeProps: parseAndFormatAmount('montantRevenusAnnuels') || 0,
				liquidAssetAmountProps: parseAndFormatAmount('montantActifsLiquides') || 0,
				annualFinancialCommitmentAmountProps:
					parseAndFormatAmount('montantEngagementsFinanciers') || 0
			}
		});
		recuResultatsSimulationProps.introduction = introductionMessage;
		recuResultatsSimulationProps.title = $t('kyc.steps.simulator.result.title', {
			values: {
				amount: formatAmountWithCurrency(currentSimulatorStep / 10 || 0)
			}
		});
	}

	return {
		donneesFinancieres: partageDonneesFinancieresValue
			? {
					montantRevenusAnnuels: montantRevenusAnnuelsProps
						? parseInt(montantRevenusAnnuelsProps.value) || 0
						: 0,
					montantActifsLiquides: montantActifsLiquidesProps
						? parseInt(montantActifsLiquidesProps.value) || 0
						: 0,
					montantEngagementsFinanciers: montantEngagementsFinanciersProps
						? parseInt(montantEngagementsFinanciersProps.value) || 0
						: 0,
					recuResultatsSimulation: recuResultatsSimulationValue,
					partageDonneesFinancieres: partageDonneesFinancieresValue
				}
			: {
					recuResultatsSimulation: recuResultatsSimulationValue,
					partageDonneesFinancieres: partageDonneesFinancieresValue
				}
	};
};

export const findUnansweredQuestion = (
	questions: KYCQuestionProps[]
): KYCQuestionProps | undefined => {
	let unanswered;
	for (const [index, { props }] of questions.entries()) {
		const hasCustomerResponse =
			props?.customerResponse &&
			(props?.customerResponse?.responses ||
				props?.customerResponse?.response ||
				props?.customerResponse?.response?.file ||
				JSON.stringify(props?.customerResponse) !== '{}');
		// exclude file and addressProof type for init index ?
		if (
			(!hasCustomerResponse &&
				props?.isVisible !== false &&
				props?.type !== KYCQuestionType.INPUT_FILE &&
				props?.type !== KYCQuestionType.INPUT_ADDRESSPROOF) ||
			(isCompanyFile(props) && !props?.readonly) ||
			(props?.type == KYCQuestionType.INPUT_FILE && props.id == 2 && !props?.readonly)
		) {
			if (props?.questions) {
				unanswered = { ...findUnansweredQuestion(props?.questions), index: index };
			}

			if (props?.required) {
				unanswered = unanswered ?? { ...props, index: index };
				break;
			}
		}
	}

	return unanswered;
};

export const calculateProgressForMobile = (
	questions: KYCQuestionProps[],
	currentStep: number,
	currentQuestion: number
): number => {
	const filteredQuestions = questions.slice(0, currentStep + 1);
	const answeredQuestions = filteredQuestions.reduce((acc, section, index) => {
		if (index < currentStep) {
			return acc + (section.questions ? section.questions.length : 0);
		} else if (index === currentStep) {
			return acc + currentQuestion;
		} else {
			return acc;
		}
	}, 0);
	const totalQuestions = questions.reduce((acc, section) => {
		return acc + (section.questions ? section.questions.length : 0);
	}, 0);
	const progressPercentage = totalQuestions === 0 ? 0 : (answeredQuestions / totalQuestions) * 100;
	return progressPercentage;
};

export const isUserBeneficiary = (user: AccountModel): boolean => {
	return (
		user?.legalPerson?.physicalPersonPartners?.some(
			(partner) =>
				partner?.lastName === user?.identity?.lastName &&
				partner.firstName === user?.identity?.firstName
		) || false
	);
};

export const processDocument = (questions: KYCQuestionProps[], key) => {
	const props = findPropsByApiKey(questions, key);
	return props && props.value && props.value[0] !== undefined ? props.value[0] : null;
};
export const filterUserDocuments = (questions: KYCQuestionProps[], user: AccountModel) => {
	// Actuellement impossible de changer un Kbis, Rbe, ou statuts après un premier envoi à l'API. Afin de pouvoir quand même modifier
	// les autres informations j'exclu ces inputs si ils sont déja connu dans le user
	const userDocuments = [
		!user?.documents?.rbe?.status ? findPropsByApiKey(questions, 'rbe') : null,
		!user?.documents?.kbis?.status ? findPropsByApiKey(questions, 'kbis') : null,
		!user?.documents?.statuts?.status ? findPropsByApiKey(questions, 'statuts') : null
	].filter((document) => document !== null);
	return userDocuments;
};

export const isUninitializedDocumentQuestion = (question: KYCQuestionProps) => {
	const { type, readonly } = question;

	const isFileInput = type === KYCQuestionType.INPUT_FILE;
	const isAddressProofInput = type === KYCQuestionType.INPUT_ADDRESSPROOF;
	const isWritable = !readonly;

	return (isFileInput || isAddressProofInput) && isWritable;
};

export const getUninitializedDocuments = (questions: KYCQuestionProps[]): KYCQuestionProps[] => {
	const questionsWithIndex = questions.map((question, index) => ({
		question: { ...question?.props, index }
	}));
	const uninitializedQuestion = questionsWithIndex.filter(({ question }) =>
		isUninitializedDocumentQuestion(question)
	);

	return uninitializedQuestion;
};
export const generateBeneficiaryQuestions = (numberOfBeneficiaries: number): KYCQuestionProps[] => {
	const beneficiaryQuestions: [] = [];

	for (let index = 0; index < numberOfBeneficiaries; index++) {
		const positionSuffix = index === 0 ? 'er' : 'e';
		const position = `${index + 1}${positionSuffix}`;
		let questions = definedQuestions(configBeneficiary()).questions;
		if (questions) {
			questions = definedBeneficiariesPositionLabel(questions, position);
			beneficiaryQuestions.push(...questions);
		}
	}

	return beneficiaryQuestions;
};
export const generateBeneficiaryQuestionsWithDatas = (
	arrayBeneficiaries: PhysicalPersonPartner[]
): KYCQuestionProps[] => {
	const beneficiaryQuestions: [] = [];
	arrayBeneficiaries.forEach((beneficiary, index) => {
		const positionSuffix = index === 0 ? 'er' : 'e';
		const position = `${index + 1}${positionSuffix}`;
		let questions = definedQuestions(configBeneficiary(beneficiary, true)).questions;
		if (questions) {
			questions = definedBeneficiariesPositionLabel(questions, position);
			beneficiaryQuestions.push(...questions);
		}
	});
	return beneficiaryQuestions;
};

const definedBeneficiariesPositionLabel = (
	questions: KYCQuestionProps[],
	position: string
): KYCQuestionProps[] => {
	const $t = get(t);
	if (questions && questions[0].props && questions[1].props) {
		questions[0].props.title = $t('kyc.steps.legalPerson.partnerInformationsTitle', {
			values: { position }
		});
		questions[1].props.title = $t('kyc.steps.legalPerson.partnerDocumentsTitle', {
			values: { position }
		});
	}

	return questions;
};
export const generateInputValidation = (currentQuestion: KYCQuestionProps): KYCQuestionProps[] => {
	let inputsValidation = [];
	if (currentQuestion?.props?.questions) {
		inputsValidation = [...currentQuestion.props.questions].map((question) => question.props);
		if (currentQuestion?.props?.required) {
			inputsValidation.push({
				...currentQuestion?.props
			});
		}
	} else {
		inputsValidation = [currentQuestion?.props];
	}
	return inputsValidation;
};

export const shouldGenerateNewBeneficiaries = (
	arrayBeneficiaries: any[],
	numberOfBeneficiary: number,
	isBeneficiaryPropsValue: string,
	user: AccountModel
): boolean => {
	let isBeneficiary = user?.legalPerson?.physicalPersonPartners?.some(
		(beneficiary) =>
			beneficiary?.lastName === user?.identity?.lastName &&
			beneficiary?.firstName === user?.identity?.firstName
	);

	return (
		arrayBeneficiaries?.length !== numberOfBeneficiary ||
		isBeneficiaryPropsValue !== isBeneficiary?.toString()
	);
};

export const docIsInvalid = (doc: DocumentsKYCModel) => {
	return doc?.status === DocumentStatusEnum.INVALID;
};
export const isDocumentRequiredForStatus = (idProps: InputFilesProps): boolean => {
	return (
		idProps.state === DocumentStatusEnum.INVALID ||
		idProps.state === DocumentStatusEnum.EXPIRED ||
		idProps.state === DocumentStatusEnum.EXPIRES_SOON
	);
};

export const isCompanyFile = (questionProps: KYCQuestionProps) => {
	if (questionProps?.apiKeys?.length) {
		return (
			questionProps?.apiKeys[1] === 'rbe' ||
			questionProps?.apiKeys[1] === 'statuts' ||
			questionProps?.apiKeys[1] === 'kbis' ||
			questionProps?.apiKeys[1] === 'comptesAnnuels'
		);
	}
};

export const submitProfile = async (
	categorie: KYCModel,
	$t: MessageFormatter,
	regulation: OperationRegulationsEnum
) => {
	const responseTemplate: KYCRequestBody = {
		type: regulation,
		questions: []
	};
	if (categorie?.questions) {
		categorie?.questions.map((question) => {
			if (question?.props && question?.props.isVisible) {
				const response = {
					...transformKYCQuestionPropsToAPI(question?.props),
					id: question?.props?.id,
					type: question?.props?.type
						? translateQuestionTypeToAPI(question?.props?.type)
						: undefined,
					libelle: question?.props?.title
				};
				responseTemplate.questions.push(response);
			}
		});
	}
	try {
		if (responseTemplate.questions.length !== 0) {
			const res = await fetch(APIEndpoints.ACCOUNT('kyc'), {
				method: 'PATCH',
				body: JSON.stringify(responseTemplate)
			});
			if (!res.ok) {
				const json = await res.json();
				error(res.status, json);
			}
		}
		await invalidateAll();
		snackbar.success($t('profile.section.security.success'));
	} catch (error: HttpError) {
		snackbar.danger(error.body?.message ?? $t('kyc.steps.profile.errors.default'));
	}
};

export const getUrlKYC = (): string => {
	if (browser) {
		const operation = get(operationStore);
		const user = get(page).data?.user;

		const regulation = operation?.regulation ?? user?.KYCPreferenceType;

		return regulation ? urls.KYC_REGULATION(regulation) : urls.KYC;
	} else {
		return urls.KYC;
	}
};

export const identifyIfAllQuestionsVisibleHasValue = (
	KYCData: KYCModel[]
): {
	knowledgeCompleted: boolean;
	experienceCompleted: boolean;
	objectiveCompleted: boolean;
	financialSituationCompleted: boolean;
} => {
	const completionResults = {
		knowledgeCompleted: true,
		experienceCompleted: true,
		objectiveCompleted: true,
		financialSituationCompleted: true
	};

	for (const category of KYCData) {
		for (const question of category.questions || []) {
			if (
				question?.customerResponse?.responses === '' ||
				(question?.isVisible && Object.keys(question.customerResponse || {}).length === 0)
			) {
				switch (category?.name) {
					case KYCCategoriesName.Experience:
						completionResults.experienceCompleted = false;
						break;
					case KYCCategoriesName.Knowledge:
						completionResults.knowledgeCompleted = false;
						break;
					case KYCCategoriesName.Objectives:
						completionResults.objectiveCompleted = false;
						break;
					case KYCCategoriesName.FinancialSituation:
						completionResults.financialSituationCompleted = false;
						break;
				}
				break;
			}
		}
	}

	return completionResults;
};
